import {Amount} from "./Amount";
import {Textarea} from "./Textarea";

export interface SpreadsheetConfig {
    table: Element;
    storage: HTMLInputElement;
    cells: NodeListOf<HTMLElement>
}

type SpreadsheetData = {
    settings: {
        [key: string]: number
    };
    content: {
        [key: number]: {
            [key: number]: string
        }
    }
}

export class Spreadsheet {
    private readonly elements: {
        table: Element,
        storage: HTMLInputElement,
        cells: NodeListOf<HTMLElement>
    };
    private controls: {
        [key: string]: Amount
    };

    constructor({table, storage, cells}: SpreadsheetConfig) {
        this.elements = {table, storage, cells};
        this.controls = {};
        cells.forEach(cell => this.bind(cell));
    }

    public addControls(type: string, element: HTMLElement, min: number, max: number): void {
        this.controls[type] = new Amount({element, min, max});
        const buttons = element.querySelectorAll(`[data-action]`);
        buttons.forEach(btn => {
            btn.addEventListener(`click`, () => this.updateTableStructure(type, btn))
        });
    }

    public updateTableStructure(type: string, trigger: Element): void {
        const action = trigger.getAttribute(`data-action`);
        const data = this.getData();
        let value = this.actions().get(action)(data, type);

        this.controls[type].update(`${value}`);
        this.setData(data);
    }

    protected actions(): Map<string, (data: SpreadsheetData, type: string) => number> {
        return new Map()
            .set(`add`, this.addStructure.bind(this))
            .set(`subtract`, this.subtractStructure.bind(this));
    }

    protected addStructure(data: SpreadsheetData, type: string): number {
        let settings  = Object.assign({}, data.settings);

        const updateData = (limit: number, callback: () => void) => {
            if (settings[type] < limit) {
                callback();
                ++settings[type];
            }
        };

        switch (type) {
            case `columns`:
                updateData(this.controls.columns.max,() => {
                    const rows = this.elements.table.querySelectorAll(`tr`);

                    for (let i = 0; i < settings.rows; i++) {
                        data.content[i][settings[type]] = ``;
                        this.createNewCell(rows[i], i, settings[type]);
                    }
                });
                break;
            case `rows`:
                updateData(this.controls.rows.max, () => {
                    const table = this.elements.table.querySelector(`tbody`);

                    data.content[settings[type]] = {};
                    for (let i = 0; i < settings.columns; i++) {
                        data.content[settings[type]][i] = ``;
                    }
                    this.createNewRow(table, settings.columns, settings[type]);
                });
                break;
        }

        data.settings = settings;
        return settings[type];
    }

    protected subtractStructure(data: SpreadsheetData, type: string): number {
        let settings  = Object.assign({}, data.settings);

        const updateData = (limit: number, callback: () => void) => {
            if (settings[type] > limit) {
                --settings[type];
                callback();
            }
        };

        switch (type) {
            case `columns`:
                updateData(1, () => {
                    const rows = this.elements.table.querySelectorAll(`tr`);
                    for (let i = 0; i < settings.rows; i++) {
                        delete data.content[i][settings[type]];
                        rows[i].lastChild.remove();
                    }
                });
                break;
            case `rows`:
                updateData(1, () => {
                    const rows = this.elements.table.querySelectorAll(`tr`);
                    for (let i = 0; i < settings.columns; i++) {
                        delete data.content[settings[type]];
                    }
                    rows[settings[type]].remove();
                });
                break;
        }

        data.settings = settings;
        return settings[type];
    }

    public updateTableData(element: HTMLTextAreaElement): void {
        const key = +element.getAttribute(`data-key`);
        const id = +element.getAttribute(`data-id`);

        let data = this.getData();
        data.content[key][id] = element.value;
        this.setData(data);
    }

    protected createNewCell(row: HTMLElement, key: number, id: number): void {
        let cell = document.createElement('td');
        cell.className = `spreadsheet__data`;
        cell.innerHTML = `<textarea class="spreadsheet__textarea" rows="1" data-key="${key}" data-id="${id}"> </textarea>`;

        Promise.resolve(row.append(cell))
            .then(() => new Textarea({elements: `textarea`, container: cell}))
            .then(() => this.bind(cell));
    }

    protected createNewRow(table: HTMLElement, key: number, k: number): void {
        let row = document.createElement('tr');
        row.className = `spreadsheet__row`;
        for (let i = 0; i < key; i++) {
            this.createNewCell(row, k, i);
        }
        table.append(row);
    }

    public setFocus(target: HTMLTextAreaElement): void {
        target.focus();
        target.setSelectionRange(target.value.length, target.value.length);
    }

    protected getData(): SpreadsheetData {
        return JSON.parse(this.elements.storage.value);
    }

    protected setData(data: SpreadsheetData): void {
        this.elements.storage.value = JSON.stringify(data);
    }

    protected bind(cell: HTMLElement): void {
        const textarea = cell.querySelector(`textarea`);

        textarea.addEventListener(`blur`, (evt) => this.updateTableData(textarea));
        cell.addEventListener(`click`, (evt) => this.setFocus(textarea));
    }
}